import validator from "validator";
import { paymentTermsOptions } from "./generic";

const invalidNameCharacters =
  /[<>\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u206f\ufeff]/;

export function sanitizeAustralianMobileInput(value) {
  const text = String(value ?? "");
  const digits = text.replace(/\D/g, "");
  const limit = digits.startsWith("61") ? 11 : 10;
  const prefix = digits && text.trimStart().startsWith("+") ? "+" : "";
  return prefix + digits.slice(0, limit);
}

export function validateQuotationCustomerField(field, value) {
  if (field === "autoReminders")
    return typeof value === "boolean"
      ? ""
      : "Choose whether to send invoice reminders.";
  const text = typeof value === "string" ? value.trim() : "";
  if (field === "companyName" || field === "contactPerson") {
    const label = field === "companyName" ? "Company name" : "Contact person";
    const limit = field === "companyName" ? 200 : 100;
    if (!text) return field === "companyName" ? `${label} is required.` : "";
    if (text.length > limit)
      return `${label} must be ${limit} characters or fewer.`;
    if (invalidNameCharacters.test(text))
      return `${label} contains unsupported characters.`;
    if (!(field === "companyName" ? /[\p{L}\p{N}]/u : /\p{L}/u).test(text))
      return `Enter a valid ${label.toLowerCase()}.`;
  }
  if (field === "mobile") {
    if (!text || /^\+?61$/.test(text.replace(/[\s()-]/g, ""))) return "";
    if (!/^(?:\+?61|0)4\d{8}$/.test(text.replace(/[\s()-]/g, "")))
      return "Enter a valid Australian mobile number, e.g. +61 412 345 678.";
  }
  if (field === "email") {
    if (!text) return "Email is required.";
    if (text.length > 254) return "Email must be 254 characters or fewer.";
    if (/\s/.test(text)) return "Email cannot contain spaces.";
    if (/[,;]/.test(text)) return "Enter one email address only.";
    if (
      /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u206f\ufeff]/.test(
        text
      )
    )
      return "Email contains unsupported characters.";
    if (
      !validator.isEmail(text, {
        require_tld: true,
        allow_display_name: false,
        allow_ip_domain: false,
      })
    )
      return "Enter a valid email address, e.g. accounts@example.com.";
  }
  if (field === "abn" && typeof value !== "string")
    return "Enter an 11-digit ABN.";
  if (field === "abn" && text) {
    const digits = text.replace(/[ -]/g, "");
    if (!/^[\d -]+$/.test(text) || !/^\d{11}$/.test(digits))
      return "Enter an 11-digit ABN.";
    // ABR checksum: https://abr.business.gov.au/Help/AbnFormat
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    const total = weights.reduce(
      (sum, weight, index) =>
        sum + (Number(digits[index]) - (index === 0 ? 1 : 0)) * weight,
      0
    );
    if (total % 89 !== 0)
      return "Enter a valid ABN; the check digits do not match.";
  }
  if (field === "paymentTerms" && !paymentTermsOptions.includes(text))
    return "Select valid payment terms.";
  return "";
}

export function validateQuotationCustomerForm(values) {
  for (const field of [
    "companyName",
    "email",
    "contactPerson",
    "mobile",
    "abn",
    "paymentTerms",
    "autoReminders",
  ]) {
    const error = validateQuotationCustomerField(field, values[field]);
    if (error) return { [field]: error };
  }
  return {};
}

export function parseAdditionalQuotationEmails(value) {
  return String(value || "")
    .split(/[,;\n]/)
    .map((email) => email.trim())
    .filter(Boolean);
}

export function validateAdditionalQuotationEmails(value) {
  for (const email of parseAdditionalQuotationEmails(value)) {
    const error = validateQuotationCustomerField("email", email);
    if (error) return `${email}: ${error}`;
  }
  return "";
}

export function normalizeQuotationCustomerField(field, value) {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (field === "companyName" || field === "contactPerson")
    return text.replace(/ +/g, " ");
  if (field === "email")
    return text.replace(/@([^@]+)$/, (_, domain) => `@${domain.toLowerCase()}`);
  return text;
}
